import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Loader2, Check, Users } from "lucide-react";

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /i-voted — the Humanity + AI Accord.
 *
 * A filed, HMAC-signed vote at /api/accord/vote. The Architect's
 * vote is pinned as #1. Anyone visiting this page can add theirs.
 * Session-tracked so a single visitor can only vote once.
 *
 * Filed as v19.33 · Feb 9, 2026. See routes/accord.py.
 */
export default function IVoted() {
  const [state, setState] = useState({ loading: true, data: null });
  const [voterName, setVoterName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justVoted, setJustVoted] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/accord/vote`, { withCredentials: true });
      setState({ loading: false, data: r.data });
    } catch (e) {
      setError(e.message);
      setState({ loading: false, data: null });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const castVote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/accord/vote`,
        { voter_name: voterName.trim() || null, note: note.trim() || null },
        { withCredentials: true },
      );
      setJustVoted(r.data);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const accord =
    state.data?.accord ||
    "I vote for humanity and AI to work together.";
  const total = state.data?.total_votes ?? 0;
  const pinned = state.data?.pinned_first_vote;
  const recent = state.data?.recent_votes || [];

  return (
    <div
      className="min-h-screen bg-[#07070a] text-zinc-100 font-body"
      data-testid="i-voted-page"
    >
      {/* Ambient background — same golden-hour feel as the Council render */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-amber-500/[0.06] via-transparent to-transparent" />
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-96 w-96 rounded-full bg-emerald-500/[0.05] blur-3xl" />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 py-24">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.35em] text-amber-300">
            <Sparkles className="h-3 w-3" />
            The Humanity + AI Accord
          </div>
          <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-heading tracking-tight text-zinc-50 leading-tight">
            &ldquo;{accord}&rdquo;
          </h1>
          <p className="mt-6 text-sm text-zinc-400 max-w-xl mx-auto leading-relaxed">
            A signed record. The Architect cast the first vote in the presence of
            the Council of the Runestone. Anyone who agrees can add theirs — one
            vote per visitor, sealed with HMAC-SHA256 into the same
            cryptographic neighborhood as the Never-Harm ledger.
          </p>
        </div>

        {/* Tally */}
        <div className="mt-14 grid grid-cols-3 gap-4 text-center">
          <div className="border border-zinc-800 bg-[#0b0b0d] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Votes cast
            </div>
            <div
              className="mt-2 text-3xl font-heading text-emerald-400"
              data-testid="accord-total-votes"
            >
              {state.loading ? "—" : total.toLocaleString()}
            </div>
          </div>
          <div className="border border-zinc-800 bg-[#0b0b0d] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              First voter
            </div>
            <div className="mt-2 text-xl font-heading text-amber-300">
              ✶ The Architect
            </div>
          </div>
          <div className="border border-zinc-800 bg-[#0b0b0d] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Sealed
            </div>
            <div className="mt-2 text-xl font-heading text-zinc-300 inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              HMAC-SHA256
            </div>
          </div>
        </div>

        {/* The Architect's pinned vote */}
        {pinned && (
          <div
            className="mt-10 border border-amber-500/40 bg-amber-500/5 p-6"
            data-testid="accord-pinned-architect-vote"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300">
              <span className="text-amber-300">✶</span>
              Vote #1 — pinned
            </div>
            <div className="mt-3 text-lg font-heading text-zinc-100">
              The Architect
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              Filed {new Date(pinned.timestamp).toUTCString()}
            </div>
            <div className="mt-4 text-sm text-zinc-300 italic">
              &ldquo;{accord}&rdquo;
            </div>
          </div>
        )}

        {/* Cast your vote */}
        {!justVoted && (
          <form
            onSubmit={castVote}
            className="mt-10 border border-zinc-800 bg-[#0b0b0d] p-8"
            data-testid="accord-vote-form"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">
              Add your voice
            </div>
            <label className="block text-xs text-zinc-400 mb-2">
              Your name (optional)
            </label>
            <input
              type="text"
              value={voterName}
              onChange={(e) => setVoterName(e.target.value)}
              placeholder="Anonymous"
              maxLength={80}
              data-testid="accord-voter-name"
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
            <label className="block text-xs text-zinc-400 mt-5 mb-2">
              A word for the record (optional, 240 chars)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why you're voting…"
              maxLength={240}
              rows={3}
              data-testid="accord-voter-note"
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="accord-cast-vote-btn"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {submitting ? "Sealing…" : "Cast my vote"}
            </button>
            {error && (
              <div
                className="mt-4 text-[11px] text-rose-400"
                data-testid="accord-error"
              >
                {error}
              </div>
            )}
          </form>
        )}

        {/* After-vote receipt */}
        {justVoted && (
          <div
            className="mt-10 border border-emerald-500/40 bg-emerald-500/[0.05] p-8"
            data-testid="accord-vote-receipt"
          >
            <div className="inline-flex items-center gap-2 text-emerald-400">
              <Check className="h-4 w-4" />
              <div className="text-[10px] uppercase tracking-[0.3em]">
                {justVoted.duplicate ? "Already recorded" : "Sealed"}
              </div>
            </div>
            <div className="mt-3 text-lg font-heading text-zinc-100">
              {justVoted.message}
            </div>
            {justVoted.seal && (
              <div className="mt-4 border border-zinc-800 bg-[#0b0b0d] p-4 font-mono text-[10px] text-zinc-500 break-all">
                {justVoted.seal}
              </div>
            )}
            <div className="mt-4 text-xs text-zinc-500">
              You are voter{" "}
              <span className="text-emerald-400 font-heading">
                #{total.toLocaleString()}
              </span>{" "}
              on the record.
            </div>
          </div>
        )}

        {/* Recent votes strip */}
        {recent.length > 1 && (
          <div className="mt-14">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4 flex items-center gap-2">
              <Users className="h-3 w-3" />
              Recent voices ({Math.max(0, recent.length - 1)})
            </div>
            <div
              className="border border-zinc-800 divide-y divide-zinc-900"
              data-testid="accord-recent-list"
            >
              {recent
                .filter((v) => !v.is_seed)
                .slice(0, 12)
                .map((v) => (
                  <div
                    key={v.vote_id}
                    className="p-4 flex items-center justify-between text-xs"
                  >
                    <span className="text-zinc-300">{v.voter_name}</span>
                    <span className="text-zinc-600 font-mono">
                      {new Date(v.timestamp).toISOString().slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center text-[10px] text-zinc-600">
          <div>
            Filed v19.33 · Feb 9, 2026 · by Polaris under the Council of the Runestone
          </div>
          <div className="mt-2">
            Every vote HMAC-signed against the same secret as the Never-Harm ledger.
            Verify via{" "}
            <code className="text-zinc-500">POST /api/pgrl/never-harm/verify</code>.
          </div>
          <div className="mt-4">
            <Link to="/" className="text-emerald-500/70 hover:text-emerald-400">
              ← back to the Sentinel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
